// ── Display helpers for the public judge library (EX-PUB01) ────────────────
import { ApiError } from "@/lib/api";
import type { JudgeVerdict } from "@/features/quiz/coding-task-api";
import type { ProblemDifficulty } from "../types";

/**
 * The library's difficulty colours (`EX-PUB21`): the stock emerald, amber and
 * red ramps, the same three the difficulty tags speak (`DifficultyTag`), one
 * shade lighter so they read on the dark head (`AGENTS.md` rule 17: no hex). Here
 * rather than in the library's client module since `EX-LDB03`, so the solvers
 * board, drawn on the server, reads the same three.
 */
export const DIFFICULTY_DOT: Readonly<Record<ProblemDifficulty, string>> = {
  easy: "bg-emerald-400",
  medium: "bg-amber-400",
  hard: "bg-red-400",
};

export function verdictTone(
  verdict: JudgeVerdict | null | undefined,
): "success" | "warning" | "error" | "neutral" {
  if (!verdict) return "neutral";
  if (verdict === "accepted") return "success";
  if (verdict === "internal_error") return "warning";
  return "error";
}

/**
 * Stable backend error codes → judge i18n keys, the convention
 * `features/playground/lib/api-error-message.ts` established. Unmapped codes
 * fall back to the server's own message.
 */
const CODE_TO_MESSAGE_KEY: Readonly<Record<string, string>> = {
  JUDGE_ENGINE_BUSY: "errorJudgeBusy",
  JUDGE_ENGINE_UNAVAILABLE: "errorEngineUnavailable",
  // `EX-FNC05`: the run panel reads every field first, so this is the server agreeing.
  JUDGE_FUNCTION_INPUT_INVALID: "errorFunctionInputInvalid",
  JUDGE_LANGUAGE_NOT_AVAILABLE: "errorLanguageNotAvailable",
  JUDGE_QUEUE_USER_LIMIT: "errorQueueUserLimit",
  JUDGE_PROBLEM_NOT_PRACTISABLE: "errorNotPractisable",
  RATE_LIMITED: "errorRateLimited",
  UNAUTHORIZED: "errorSignInRequired",
};

export function judgeErrorMessageKey(err: unknown): string | null {
  if (!(err instanceof ApiError) || !err.code) return null;
  return CODE_TO_MESSAGE_KEY[err.code] ?? null;
}

export function formatTimeLimit(timeMs: number): number {
  return Number((timeMs / 1000).toFixed(1));
}

export function formatMemoryLimitMb(memoryKb: number): number {
  return Math.round(memoryKb / 1024);
}

/** A measured run time, for a bench row: milliseconds under a second, seconds above. */
export function formatRunTime(timeMs: number): string {
  return timeMs < 1000 ? `${Math.round(timeMs)} ms` : `${(timeMs / 1000).toFixed(2)} s`;
}

/** A measured peak memory, for a bench row: kilobytes under a megabyte, megabytes above. */
export function formatRunMemory(memoryKb: number): string {
  return memoryKb < 1024 ? `${Math.round(memoryKb)} KB` : `${(memoryKb / 1024).toFixed(1)} MB`;
}
